import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Music2 } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { MusicLiveDemo } from '../../components/MusicLiveDemo';

// Nomes reais dos gêneros (fragment_music_list.xml). A descrição de humor ao
// lado de cada um é só uma legenda editorial nossa — o nome do gênero é real.
const generos = [
  { name: 'Coral Infantil', mood: 'Para os menores' },
  { name: 'Clássico Suave', mood: 'Foco tranquilo' },
  { name: 'Pop Alegre', mood: 'Energia positiva' },
  { name: 'Natureza Acústico', mood: 'Relaxamento profundo' },
  { name: 'Eletrônico Calmo', mood: 'Concentração' },
  { name: 'Lo-fi', mood: 'Estudar ou descansar' },
  { name: 'Jazz', mood: 'Ambiente sofisticado' },
  { name: 'Rock Leve', mood: 'Animação sem exagero' },
];

const MusicaPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden text-center">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <div
            className="w-[380px] h-[380px] rounded-full opacity-25 blur-3xl"
            style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative container mx-auto px-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <Music className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
            Música Terapêutica
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl mx-auto leading-[1.1]">
            Relaxar e se concentrar com <span className="text-primary-ink dark:text-primary-tint">música</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl mx-auto">
            Composições com IA organizadas por gênero, para relaxar, focar ou se divertir.
          </p>

          <div className="mt-12 max-w-sm mx-auto">
            <MusicLiveDemo />
          </div>
        </div>
      </section>

      {/* Grade de gêneros */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-2">
              8 gêneros para cada momento
            </h2>
            <p className="text-muted-foreground text-center max-w-xl mx-auto mb-10">
              De coral infantil a lo-fi — pensados pra cobrir momentos diferentes do dia.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {generos.map((genero) => (
                <div key={genero.name} className="rounded-2xl bg-card shadow-sm p-5 flex flex-col items-center text-center gap-2">
                  <span className="w-11 h-11 rounded-xl bg-primary/10 dark:bg-primary/15 flex items-center justify-center">
                    <Music2 className="w-5 h-5 text-primary-ink dark:text-primary-tint" />
                  </span>
                  <h3 className="font-semibold text-foreground text-sm">{genero.name}</h3>
                  <p className="text-xs text-muted-foreground">{genero.mood}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Sobre o player */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Um player pensado pra criança usar sozinha
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Sem menus complexos: a faixa e o gênero atual aparecem de forma simples e
              direta, com controles grandes o bastante pra funcionar sem ajuda de um adulto.
              As músicas mais tocadas entram depois no painel de Acompanhamento de Progresso,
              dando à família uma pista do que mais relaxa ou engaja a criança.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/progresso"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer o Acompanhamento de Progresso
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MusicaPage;
